import type { LinkHTMLAttributes } from 'react';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type {
  FrontofficeWebSeoArticleAuthorGetByUri200Content,
  FrontofficeWebSeoArticleAuthorGetByUriParams,
  FrontofficeWebSeoArticleGetByUri200Content,
  FrontofficeWebSeoArticleGetByUriParams,
  FrontofficeWebSeoArticleTagGetByUri200Content,
  FrontofficeWebSeoArticleTagGetByUriParams,
  FrontofficeWebSeoCategoryGetByUri200Content,
  FrontofficeWebSeoCategoryGetByUriParams,
  FrontofficeWebSeoDocumentGetByUri200Content,
  FrontofficeWebSeoDocumentGetByUriParams,
  FrontofficeWebSeoRegionGetByUri200Content,
  FrontofficeWebSeoRegionGetByUriParams,
} from 'generated/api/frontofficeAPI.schemas';
import {
  frontofficeWebSeoArticleAuthorGetByUri,
  frontofficeWebSeoArticleGetByUri,
  frontofficeWebSeoArticleTagGetByUri,
  frontofficeWebSeoCategoryGetByUri,
  frontofficeWebSeoDocumentGetByUri,
  frontofficeWebSeoRegionGetByUri,
} from 'generated/api/web';
import { LOCATION_CHANGE } from 'lib/reduxRouter';

import type { EnrichedLocationChangeAction } from 'store/locationChangeEnrichMiddleware';
import type { LoosePayloadAction } from 'store/utils';
import { createAsyncActions } from 'store/utils';

import type { SeoStateType, SeoType } from './types';

const moduleName = 'seo';

export const initialState: SeoStateType = {
  meta: null,
  links: undefined,
  image: undefined,
  ogType: undefined,
};

export const getSeoCategoryByUriActions = createAsyncActions<
  FrontofficeWebSeoCategoryGetByUriParams,
  FrontofficeWebSeoCategoryGetByUri200Content
>({
  moduleName,
  name: 'GET_SEO_CATEGORY_BY_URI',
  api: frontofficeWebSeoCategoryGetByUri,
});

export const getSeoRegionByUriActions = createAsyncActions<
  FrontofficeWebSeoRegionGetByUriParams,
  FrontofficeWebSeoRegionGetByUri200Content
>({
  moduleName,
  name: 'GET_SEO_REGION_BY_URI',
  api: frontofficeWebSeoRegionGetByUri,
});

export const getSeoDocumentByUriActions = createAsyncActions<
  FrontofficeWebSeoDocumentGetByUriParams,
  FrontofficeWebSeoDocumentGetByUri200Content
>({
  moduleName,
  name: 'GET_SEO_DOCUMENT_BY_URI',
  api: frontofficeWebSeoDocumentGetByUri,
});

export const getSeoArticleByUriActions = createAsyncActions<
  FrontofficeWebSeoArticleGetByUriParams,
  FrontofficeWebSeoArticleGetByUri200Content
>({
  moduleName,
  name: 'GET_SEO_ARTICLE_BY_URI',
  api: frontofficeWebSeoArticleGetByUri,
});

export const getSeoArticleTagByUriActions = createAsyncActions<
  FrontofficeWebSeoArticleTagGetByUriParams,
  FrontofficeWebSeoArticleTagGetByUri200Content
>({
  moduleName,
  name: 'GET_SEO_ARTICLE_TAG_BY_URI',
  api: frontofficeWebSeoArticleTagGetByUri,
});

export const getSeoArticleAuthorByUriActions = createAsyncActions<
  FrontofficeWebSeoArticleAuthorGetByUriParams,
  FrontofficeWebSeoArticleAuthorGetByUri200Content
>({
  moduleName,
  name: 'GET_SEO_ARTICLE_ARTICLE_BY_URI',
  api: frontofficeWebSeoArticleAuthorGetByUri,
});

const seo = createSlice({
  name: moduleName,
  initialState,
  reducers: {
    setSeo: (
      state,
      action: PayloadAction<Partial<Omit<SeoStateType, 'meta'>> & { meta: Partial<SeoType> }>
    ) => {
      const { meta, links, image, ogType } = action.payload;

      state.meta = {
        noFollow: false,
        noIndex: false,
        relinkBlocks: [],
        topRelinkBlocks: [],
        ...meta,
      };

      // NOTE: exclude defaultValue is necessary for correct typing as it may contain a read-only value
      state.links = Array.isArray(links)
        ? (links as Array<Omit<LinkHTMLAttributes<HTMLLinkElement>, 'defaultValue'>>)
        : undefined;
      state.image = image;
      state.ogType = ogType;
    },

    setSeoLinks: (
      state,
      action: PayloadAction<
        Array<Omit<LinkHTMLAttributes<HTMLLinkElement>, 'defaultValue'>> | undefined
      >
    ) => {
      state.links = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(LOCATION_CHANGE, (state, action: EnrichedLocationChangeAction) => {
      if (action.payload.isFirstRendering || action.payload.isSamePathname) {
        return;
      }

      state.meta = null;
      state.links = undefined;
      state.image = undefined;
      state.ogType = undefined;
    });
    builder.addCase(
      getSeoCategoryByUriActions.success.type,
      (state, action: LoosePayloadAction) => {
        state.meta = action.payload;
      }
    );
    builder.addCase(getSeoRegionByUriActions.success.type, (state, action: LoosePayloadAction) => {
      state.meta = action.payload;
    });
    builder.addCase(
      getSeoDocumentByUriActions.success.type,
      (state, action: LoosePayloadAction) => {
        state.meta = action.payload;
      }
    );
  },
});

export const { setSeo, setSeoLinks } = seo.actions;

export default seo.reducer;
